import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { Button, Icon, IconButton, Spinner, useApi, useAuth, useRealtimeMessages } from '@saas/ui';
import type { TelephonyRealtimeMessage } from '../../api/telephony.dto.js';
import { telephonyApi, telephonyKeys } from '../api.js';
import { CALL_READ, MATCH_KIND_LABEL, MATCHED_BY_LABEL, displayNumber, recordPath } from './labels.js';

interface Incoming {
  callId: string;
  number: string | null;
  phase: 'ringing' | 'answered' | 'ended';
  /** 自分が応答した */
  mine: boolean;
}

/** 鳴り続けて終了が届かないときに閉じるまでの時間 */
const RING_TIMEOUT_MS = 3 * 60_000;
/** 終了してから閉じるまでの時間 */
const ENDED_LINGER_MS = 6_000;
const MAX_SHOWN = 3;

function parse(payload: unknown): TelephonyRealtimeMessage | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const p = payload as Record<string, unknown>;
  if (typeof p.callId !== 'string' || typeof p.type !== 'string') return null;
  if (p.type !== 'call.ringing' && p.type !== 'call.answered' && p.type !== 'call.ended') return null;
  return {
    type: p.type,
    callId: p.callId,
    direction: p.direction === 'outbound' || p.direction === 'internal' ? p.direction : 'inbound',
    displayNumber: typeof p.displayNumber === 'string' && p.displayNumber ? p.displayNumber : null,
    startedAt: typeof p.startedAt === 'string' ? p.startedAt : new Date().toISOString(),
    answeredByUserId: typeof p.answeredByUserId === 'string' ? p.answeredByUserId : null,
  };
}

/**
 * 着信ポップ（SFA 段階 5 §9.4・02 §5.2.2）。realtime の `telephony` チャネルの call.ringing / answered / ended を受けて、
 * 画面の右上に「誰からの電話か」を出す。realtime には名前が載らない（番号と ID だけ）ので、候補は GET /telephony/calls/:id を
 * 見る人の権限で引き直す（読めない候補は件数だけ）。ほかの人が応答したら閉じ、自分が応答したら「通話中」のまま残す。
 * シェル（ログイン後のレイアウト）に 1 つだけ置く。telephony が無効・通話を読む権限が無ければ何も描かない
 */
export function TelephonyIncomingCall() {
  const { canAny, isModuleEnabled, me } = useAuth();
  const enabled = Boolean(me) && isModuleEnabled('telephony') && canAny(...CALL_READ);
  const myId = me?.user.id ?? null;
  const [calls, setCalls] = useState<Incoming[]>([]);

  useRealtimeMessages(enabled ? ['telephony'] : [], (e) => {
    const m = parse({ ...(e.payload as object), type: e.type });
    if (!m || m.direction !== 'inbound') return;
    setCalls((cur) => {
      const found = cur.find((c) => c.callId === m.callId);
      if (m.type === 'call.ringing') {
        if (found) return cur;
        return [{ callId: m.callId, number: m.displayNumber, phase: 'ringing', mine: false }, ...cur];
      }
      if (!found) return cur;
      if (m.type === 'call.answered') {
        // ほかの人（端末）が応答した → 閉じる
        if (m.answeredByUserId && m.answeredByUserId !== myId) return cur.filter((c) => c.callId !== m.callId);
        return cur.map((c) => (c.callId === m.callId ? { ...c, phase: 'answered', mine: m.answeredByUserId === myId } : c));
      }
      return cur.map((c) => (c.callId === m.callId ? { ...c, phase: 'ended' } : c));
    });
  });

  if (!enabled || calls.length === 0) return null;
  const dismiss = (id: string) => setCalls((cur) => cur.filter((c) => c.callId !== id));
  return (
    <div className="pointer-events-none fixed right-3 top-3 z-[90] flex w-[min(24rem,calc(100vw-1.5rem))] flex-col gap-2" aria-live="assertive">
      {calls.slice(0, MAX_SHOWN).map((c) => (
        <IncomingCallCard key={c.callId} call={c} onDismiss={() => dismiss(c.callId)} />
      ))}
      {calls.length > MAX_SHOWN ? <p className="pointer-events-auto self-end rounded-pill bg-card px-3 py-1 text-caption shadow-float">ほかに {calls.length - MAX_SHOWN} 件の着信</p> : null}
    </div>
  );
}

function IncomingCallCard({ call, onDismiss }: { call: Incoming; onDismiss: () => void }) {
  const api = useApi();
  const navigate = useNavigate();
  // 候補は見る人の権限で絞った詳細から（取得に失敗しても番号だけで出す）
  const q = useQuery({ queryKey: telephonyKeys.call(call.callId), queryFn: () => telephonyApi.call(api, call.callId), retry: 1, staleTime: 5_000 });

  // 鳴りっぱなし・終了後は自動で閉じる（親の再描画でタイマーを掛け直さないよう、閉じる関数は ref で持つ）
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;
  useEffect(() => {
    const t = window.setTimeout(() => dismissRef.current(), call.phase === 'ended' ? ENDED_LINGER_MS : RING_TIMEOUT_MS);
    return () => window.clearTimeout(t);
  }, [call.phase]);

  const detail = q.data;
  const matches = detail?.matches ?? [];
  const title = call.phase === 'ringing' ? '着信' : call.phase === 'answered' ? (call.mine ? '通話中' : '応答済み') : '通話が終わりました';
  const open = (to: string) => {
    navigate(to);
    if (call.phase === 'ended') onDismiss();
  };

  return (
    <section role="alertdialog" aria-label={`${title} ${displayNumber(call.number)}`} className="pointer-events-auto rounded-card border border-line bg-card p-4 shadow-float">
      <div className="flex items-start gap-3">
        <span className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-pill ${call.phase === 'ringing' ? 'animate-pulse bg-brand text-brand-on' : 'bg-sunken text-fg-secondary'}`}>
          <Icon name="call" size={20} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-caption text-fg-secondary">{title}</p>
          <p className="font-mono text-headline">{displayNumber(call.number ?? detail?.counterpart)}</p>
        </div>
        <IconButton icon="close" label="閉じる" size={20} onClick={onDismiss} />
      </div>
      <div className="mt-3">
        {q.isPending ? (
          <span className="flex items-center gap-2 text-caption text-fg-secondary">
            <Spinner size={16} /> 相手を探しています…
          </span>
        ) : matches.length > 0 ? (
          <ul className="flex flex-col gap-1.5" aria-label="相手の候補">
            {matches.slice(0, 4).map((m) => (
              <li key={`${m.kind}:${m.id}`}>
                <Link to={recordPath(m.kind, m.id)} onClick={() => call.phase === 'ended' && onDismiss()} className="block rounded-input px-2 py-1 hover:bg-sunken">
                  <span className="block text-body wrap-name">{m.label}</span>
                  <span className="block text-caption text-fg-secondary">
                    {MATCH_KIND_LABEL[m.kind]}・{MATCHED_BY_LABEL[m.matchedBy]}
                    {m.confidence === 'candidate' ? '（候補）' : ''}
                  </span>
                </Link>
              </li>
            ))}
            {matches.length > 4 ? <li className="px-2 text-caption text-fg-tertiary">ほかに {matches.length - 4} 件</li> : null}
          </ul>
        ) : (
          <p className="text-caption text-fg-secondary">{call.number ? '登録されている相手は見つかりませんでした' : '非通知の着信です'}</p>
        )}
        {detail && detail.hiddenCount > 0 ? <p className="mt-1 px-2 text-caption text-fg-tertiary">あなたには表示できない候補が {detail.hiddenCount} 件あります</p> : null}
        {detail && detail.openDeals.length > 0 ? (
          <p className="mt-1 px-2 text-caption text-fg-secondary">
            進行中の商談:{' '}
            {detail.openDeals.slice(0, 2).map((d, i) => (
              <span key={d.dealId}>
                {i > 0 ? '、' : ''}
                <Link to={recordPath('deal', d.dealId)} className="underline-offset-2 hover:underline">
                  {d.name}
                </Link>
              </span>
            ))}
            {detail.openDeals.length > 2 ? ` ほか ${detail.openDeals.length - 2} 件` : ''}
          </p>
        ) : null}
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <Button size="sm" variant="secondary" onClick={() => open(`/telephony/calls/${call.callId}`)}>
          通話を開く
        </Button>
      </div>
    </section>
  );
}
